import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { fetchPersonasVetadas, upsertPersonaVetada } from "../../services/General/personaVetadaService";
import { PersonaVetada } from "../../types/personaVetada";
import PersonaVetadaModal from "../Citas/PersonaVetadaModal";

const PersonaVetadaList: React.FC = () => {
    const { user } = useAuth();
    const [personasVetadas, setPersonasVetadas] = useState<PersonaVetada[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPersonaVetada, setSelectedPersonaVetada] = useState<PersonaVetada>({
        veto_id: 0,
        cedula: '',
        nombre: '',
        motivo: '',
        fecha_desde: new Date().toISOString().split('T')[0], // Formato YYYY-MM-DD
        observaciones: '',
        is_active: true,
        created_by: '',
        date_created: ''
    });
    const [showModal, setShowModal] = useState<boolean>(false);

    useEffect(() => {
        loadPersonasVetadas();
    }, []);

    const loadPersonasVetadas = async () => {
        setLoading(true);
        try {
            const data = await fetchPersonasVetadas();
            setPersonasVetadas(data);
        } catch (error: any) {
            setError(error.message || 'Error cargando personas vetadas');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePersonaVetada = async (personaVetada?: PersonaVetada) => {
        setSelectedPersonaVetada(personaVetada || {
            veto_id: 0,
            cedula: '',
            nombre: '',
            motivo: '',
            fecha_desde: new Date().toISOString().split('T')[0],
            observaciones: '',
            is_active: true,
            created_by: user?.User.email || '',
            date_created: ''
        });
        setShowModal(true);
    };

    const handleChangeStatusPersonaVetada = async (personaVetada: PersonaVetada) => {
        if (!personaVetada.veto_id) return;
        const action = personaVetada.is_active ? 'desactivar' : 'activar';
        if (!window.confirm(`¿Está seguro de ${action} a la persona: ${personaVetada.nombre}?`)) return;
        personaVetada.is_active = !personaVetada.is_active;
        try {
            await upsertPersonaVetada(personaVetada);
            loadPersonasVetadas();
        } catch (error: any) {
            setError(error.message || `Error al ${action} la persona vetada`);
        }
    };

    const handleSavePersonaVetada = async (personaVetada: PersonaVetada) => {
        setLoading(true);
        try {
            if (personaVetada.veto_id) {
                personaVetada.updated_by = user?.User.email || '';
            } else {
                personaVetada.created_by = user?.User.email || '';
            }
            await upsertPersonaVetada(personaVetada);
            loadPersonasVetadas();
        } catch (error: any) {
            setError(error.message || 'Error guardando la persona vetada');
        } finally {
            setLoading(false);
            setShowModal(false);
        }
    };

    if (loading) {
        return <div>Cargando Personas Vetadas...</div>;
    }
    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="container mt-4">
            <h4 className="mb-4">Mantenimiento de Personas Vetadas</h4>
            <div className="mb-3 text-end">
                <button className="btn btn-primary" onClick={() => handleCreatePersonaVetada()}>
                    Crear Persona Vetada
                </button>
            </div>
            <table className="table table-striped table-hover">
                <thead>
                    <tr>
                        <th>Id</th>
                        <th>Cédula</th>
                        <th>Nombre</th>
                        <th>Motivo</th>
                        <th>Fecha Desde</th>
                        <th>Observaciones</th>
                        <th>Activo</th>
                        <th>Fecha Creación</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {personasVetadas.map((persona) => (
                        <tr key={persona.veto_id}>
                            <td>{persona.veto_id}</td>
                            <td>{persona.cedula}</td>
                            <td>{persona.nombre}</td>
                            <td>{persona.motivo}</td>
                            <td>{new Date(persona.fecha_desde).toLocaleDateString()}</td>
                            <td>
                                {persona.observaciones && persona.observaciones.length > 50 
                                    ? `${persona.observaciones.substring(0, 50)}...`
                                    : persona.observaciones}
                            </td>
                            <td>{persona.is_active ? 'Si' : 'No'}</td>
                            <td>{new Date(persona.date_created).toLocaleString()}</td>
                            <td>
                                <button className="btn btn-sm btn-secondary me-2" onClick={() => handleCreatePersonaVetada(persona)}>Editar</button>
                                <button
                                    className={`btn btn-sm ${persona.is_active ? 'btn-danger' : 'btn-success'}`}
                                    onClick={() => handleChangeStatusPersonaVetada(persona)}
                                >
                                    {persona.is_active ? 'Desactivar' : 'Activar'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {showModal && (
                <PersonaVetadaModal
                    personaVetada={selectedPersonaVetada}
                    onClose={() => setShowModal(false)}
                    onSave={handleSavePersonaVetada}
                />
            )}
        </div>
    );
};

export default PersonaVetadaList;